import { ref, computed, watch } from 'vue'

/**
 * Cookie consent state, persisted in localStorage.
 *
 * Categories:
 *  - essential: always true (login session, security, load balancing)
 *  - analytics: opt-in (Google Analytics, etc.)
 *  - marketing: opt-in (Meta Pixel, ad attribution)
 *
 * Usage in code that loads tracking scripts:
 *
 *   import { useCookieConsent } from '@/composables/useCookieConsent'
 *   const { consent } = useCookieConsent()
 *   watch(() => consent.value.analytics, (allowed) => {
 *     if (allowed) loadGoogleAnalytics()
 *   }, { immediate: true })
 */

const STORAGE_KEY = 'manito_cookie_consent'
const CONSENT_VERSION = 1

export interface CookieConsent {
  version: number
  decided: boolean
  essential: true
  analytics: boolean
  marketing: boolean
  decidedAt: string | null
}

const defaultConsent: CookieConsent = {
  version: CONSENT_VERSION,
  decided: false,
  essential: true,
  analytics: false,
  marketing: false,
  decidedAt: null,
}

function loadConsent(): CookieConsent {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...defaultConsent }
    const parsed = JSON.parse(raw) as CookieConsent
    if (parsed.version !== CONSENT_VERSION) return { ...defaultConsent }
    return { ...defaultConsent, ...parsed, essential: true }
  } catch {
    return { ...defaultConsent }
  }
}

const consent = ref<CookieConsent>(loadConsent())
const showBanner = ref(!consent.value.decided)

watch(
  consent,
  (val) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(val))
    } catch {
      // ignore (e.g. private mode)
    }
  },
  { deep: true },
)

function acceptAll() {
  consent.value = {
    version: CONSENT_VERSION,
    decided: true,
    essential: true,
    analytics: true,
    marketing: true,
    decidedAt: new Date().toISOString(),
  }
  showBanner.value = false
}

function rejectNonEssential() {
  consent.value = {
    version: CONSENT_VERSION,
    decided: true,
    essential: true,
    analytics: false,
    marketing: false,
    decidedAt: new Date().toISOString(),
  }
  showBanner.value = false
  clearAnalyticsCookies()
}

function savePreferences(prefs: { analytics: boolean; marketing: boolean }) {
  consent.value = {
    version: CONSENT_VERSION,
    decided: true,
    essential: true,
    analytics: prefs.analytics,
    marketing: prefs.marketing,
    decidedAt: new Date().toISOString(),
  }
  showBanner.value = false
  if (!prefs.analytics) clearAnalyticsCookies()
}

/**
 * Set while something (e.g. the registration form) is blocked waiting for
 * a cookie decision — the banner uses it to visually draw attention.
 */
const bannerAttention = ref(false)
let attentionTimer: ReturnType<typeof setTimeout> | null = null

function reopenBanner(withAttention = false) {
  showBanner.value = true
  if (withAttention) {
    // Retrigger the animation even if already active
    bannerAttention.value = false
    if (attentionTimer) clearTimeout(attentionTimer)
    requestAnimationFrame(() => {
      bannerAttention.value = true
      attentionTimer = setTimeout(() => {
        bannerAttention.value = false
      }, 4000)
    })
  }
}

function clearAnalyticsCookies() {
  // Best-effort: clear common GA cookies on the current domain
  const gaCookies = ['_ga', '_gid', '_gat']
  const host = window.location.hostname
  const domains = [host, '.' + host, '.' + host.split('.').slice(-2).join('.')]
  document.cookie.split(';').forEach((c) => {
    const name = c.split('=')[0].trim()
    if (gaCookies.some((p) => name === p || name.startsWith(p + '_'))) {
      domains.forEach((d) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${d}`
      })
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
    }
  })
}

export function useCookieConsent() {
  return {
    consent: computed(() => consent.value),
    showBanner,
    bannerAttention,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    reopenBanner,
  }
}
